/**
 * Timezone-anchored calendar fields, built on `Intl.DateTimeFormat.formatToParts`.
 *
 * This module replaces the old `tzDate` helper, which formatted an instant to a
 * locale string such as "8/3/2026, 2:30:00 PM" and re-parsed it with `new Date()`.
 * Parsing a non-ISO date string is implementation defined, the round trip
 * silently discarded milliseconds, and the result depended on the ICU data
 * compiled into the runtime, so the browser, the Node server and a native
 * runtime could disagree about which day it was.
 *
 * `formatToParts` is specified behaviour and hands back numeric fields directly,
 * so there is no string to re-parse and nothing to guess.
 *
 * Audit references prevented here:
 *   utils.js #5   (tzDate built on a toLocaleString round trip)
 *   utils.js #6   (DST correction applied in one helper and not its neighbour)
 *   utils.js #10  (helpers coerced malformed input silently)
 *   utils.js #11  (five or six round trips per call on a hot path)
 */

import { APP_TZ } from './app-timezone.js';

/**
 * Formatter construction is the expensive part of `Intl`, so one formatter per
 * timezone is built once and reused. The cache is keyed by timezone and holds
 * only immutable formatter instances, so it is not shared mutable state in the
 * sense the audit warns about (theme.js #1, integrations.js #10): nothing can
 * write a different value into it and change behaviour elsewhere.
 */
const FORMATTER_BY_ZONE = new Map();

/** Numeric part types `formatToParts` gives us, in the order we read them. */
const NUMERIC_PARTS = Object.freeze(['year', 'month', 'day', 'hour', 'minute', 'second']);

function formatterFor(timeZone) {
  const cached = FORMATTER_BY_ZONE.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    // hourCycle h23 rather than hour12:false. Some ICU builds report midnight as
    // hour 24 under hour12:false, which would make an hour comparison wrong for
    // one hour a day.
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'long',
  });
  FORMATTER_BY_ZONE.set(timeZone, formatter);
  return formatter;
}

/**
 * Assert that a value is a usable instant.
 *
 * @param {unknown} instant
 * @returns {Date}
 * @throws {TypeError} if the value is not a Date, or is an Invalid Date.
 *   Deliberately loud: an Invalid Date used to flow straight into a date key and
 *   then into a database query with nothing raising (utils.js #10).
 */
export function assertInstant(instant) {
  if (!(instant instanceof Date)) {
    throw new TypeError(`Expected a Date instant, received ${typeof instant}`);
  }
  if (Number.isNaN(instant.getTime())) {
    throw new TypeError('Expected a valid Date instant, received an Invalid Date');
  }
  return instant;
}

/**
 * Calendar and wall-clock fields for an instant, as seen in a timezone.
 *
 * @typedef {object} ZonedParts
 * @property {number} year      Four digit calendar year.
 * @property {number} month     1 to 12.
 * @property {number} day       1 to 31.
 * @property {number} hour      0 to 23.
 * @property {number} minute    0 to 59.
 * @property {number} second    0 to 59.
 * @property {string} weekdayLong  For example "Monday".
 */

/**
 * Read an instant's calendar fields in a timezone.
 *
 * @param {Date} instant       The moment to read.
 * @param {string} [timeZone]  IANA zone. Defaults to the application timezone.
 * @returns {ZonedParts}       Frozen, so a caller cannot mutate a shared result.
 * @throws {TypeError} if `instant` is not a valid Date.
 * @throws {RangeError} if the runtime does not know the timezone.
 */
export function zonedParts(instant, timeZone = APP_TZ) {
  assertInstant(instant);
  const parts = formatterFor(timeZone).formatToParts(instant);
  const found = {};
  for (const part of parts) {
    if (part.type === 'weekday') found.weekdayLong = part.value;
    else if (NUMERIC_PARTS.includes(part.type)) found[part.type] = Number(part.value);
  }
  for (const type of NUMERIC_PARTS) {
    if (!Number.isInteger(found[type])) {
      throw new RangeError(`Intl did not return a numeric "${type}" for zone ${timeZone}`);
    }
  }
  return Object.freeze(found);
}

/**
 * Minutes since midnight, in the given timezone.
 *
 * @param {Date} instant
 * @param {string} [timeZone]
 * @returns {number} 0 to 1439.
 */
export function zonedMinutesOfDay(instant, timeZone = APP_TZ) {
  const { hour, minute } = zonedParts(instant, timeZone);
  return hour * 60 + minute;
}
